import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface PeriodoBanco {
  dias: number
  saldoMinutos: number
}

interface TotaisFicha {
  jornadaMin: number
  hrtbMin: number
  hcMin: number
  he50Min: number
  he100Min: number
  adnMin: number
  henMin: number
}

interface Ficha {
  ano: number
  mes: number
  colaborador: string
  nome: string
  situacao: number | null
  banco_horas?: PeriodoBanco[]
  totais?: TotaisFicha
}

const CAMPOS_TOTAIS_FICHA: { campo: keyof TotaisFicha; sigla: string; descricao: string }[] = [
  { campo: 'jornadaMin', sigla: 'Jornada', descricao: 'Jornada' },
  { campo: 'hrtbMin', sigla: 'HRTB', descricao: 'Hora Trabalhada' },
  { campo: 'hcMin', sigla: 'HC', descricao: 'Hora a Compensar' },
  { campo: 'he50Min', sigla: 'HE50', descricao: 'Hora Extra 50%' },
  { campo: 'he100Min', sigla: 'HE100', descricao: 'Hora Extra 100%' },
  { campo: 'adnMin', sigla: 'ADN', descricao: 'Adicional Noturno' },
  { campo: 'henMin', sigla: 'HEN', descricao: 'Hora Extra Noturna' },
]

function minutosParaHM(totalMin: number): string {
  const sinal = totalMin < 0 ? '-' : ''
  const abs = Math.abs(totalMin)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${sinal}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const MESES = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

function SituacaoBadge({ situacao }: { situacao: number | null }) {
  if (situacao === 0) return <span className="badge badge-success">Aberto</span>
  if (situacao === 1) return <span className="badge badge-default">Fechado</span>
  return <span className="badge badge-default">—</span>
}

// mantém os filtros dessa tela entre idas e vindas (ex: abrir uma ficha e voltar),
// pra não perder o que o digitador tinha selecionado
interface FiltroSalvo {
  ano: number | ''
  mes: number | ''
  situacao: string
  colaborador: string
  pagina: number
}

const FILTRO_STORAGE_KEY = 'fichaPonto.filtro'

// a lista vem paginada do servidor: um ano inteiro passa de 5 mil fichas
const FICHAS_POR_PAGINA = 100

function lerFiltroSalvo(): FiltroSalvo {
  try {
    const bruto = localStorage.getItem(FILTRO_STORAGE_KEY)
    if (bruto) return { ano: '', mes: '', situacao: '', colaborador: '', pagina: 1, ...JSON.parse(bruto) }
  } catch { /* ignora e usa o padrão */ }
  return { ano: new Date().getFullYear(), mes: '', situacao: '', colaborador: '', pagina: 1 }
}

export default function FichaPonto() {
  const navigate = useNavigate()
  const [ano, setAno] = useState<number | ''>(() => lerFiltroSalvo().ano)
  const [mes, setMes] = useState<number | ''>(() => lerFiltroSalvo().mes)
  const [situacao, setSituacao] = useState(() => lerFiltroSalvo().situacao)
  const [colaborador, setColaborador] = useState(() => lerFiltroSalvo().colaborador)
  const [pagina, setPagina] = useState(() => lerFiltroSalvo().pagina)
  const [total, setTotal] = useState(0)
  const [lista, setLista] = useState<Ficha[]>([])
  const [carregando, setCarregando] = useState(false)

  useEffect(() => {
    localStorage.setItem(FILTRO_STORAGE_KEY, JSON.stringify({ ano, mes, situacao, colaborador, pagina }))
  }, [ano, mes, situacao, colaborador, pagina])

  const totalPaginas = Math.max(1, Math.ceil(total / FICHAS_POR_PAGINA))

  async function carregar(paginaAlvo = pagina) {
    setCarregando(true)
    try {
      const params: Record<string, string | number> = { pagina: paginaAlvo, porPagina: FICHAS_POR_PAGINA }
      if (ano !== '') params.ano = ano
      if (mes !== '') params.mes = mes
      if (situacao !== '') params.situacao = situacao
      if (colaborador.trim()) params.colaborador = colaborador.trim()
      const r = await api.get('/cartao-ponto/fichas', { params })
      // página além do fim (ex: excluiu a última ficha da última página): volta pra última que existe
      const ultimaPagina = Math.max(1, Math.ceil(r.data.total / FICHAS_POR_PAGINA))
      if (r.data.fichas.length === 0 && paginaAlvo > ultimaPagina) {
        await carregar(ultimaPagina)
        return
      }
      setLista(r.data.fichas)
      setTotal(r.data.total)
      setPagina(paginaAlvo)
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => { carregar() }, [])

  async function excluir(f: Ficha) {
    if (!window.confirm(`Excluir a ficha ponto de ${f.colaborador} - ${f.nome} (${MESES[f.mes]}/${f.ano})? Isso remove todos os dias lançados.`)) return
    await api.delete(`/cartao-ponto/fichas/${f.ano}/${f.mes}/${f.colaborador}`)
    await carregar()
  }

  const paginacao = total > FICHAS_POR_PAGINA && (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, fontSize: '0.85rem', color: '#374151' }}>
      <button className="btn btn-outline btn-sm" disabled={carregando || pagina <= 1} onClick={() => carregar(pagina - 1)}>
        ‹ Anterior
      </button>
      <span>Página {pagina} de {totalPaginas}</span>
      <button className="btn btn-outline btn-sm" disabled={carregando || pagina >= totalPaginas} onClick={() => carregar(pagina + 1)}>
        Próxima ›
      </button>
    </div>
  )

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.6rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Ficha Ponto</h2>
        <span style={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: 'monospace' }} title="Tabela frota.cartta">CARTTA</span>
      </div>

      <div className="card" style={{ marginBottom: '1.2rem' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div className="field" style={{ width: 240 }}>
            <label>Colaborador (código ou nome)</label>
            <input
              type="text"
              value={colaborador}
              placeholder="Ex: 33 ou MARIA"
              onChange={e => setColaborador(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') carregar(1) }}
            />
          </div>
          <div className="field" style={{ width: 100 }}>
            <label>Ano</label>
            <input type="number" value={ano} onChange={e => setAno(e.target.value ? Number(e.target.value) : '')} />
          </div>
          <div className="field" style={{ width: 160 }}>
            <label>Mês</label>
            <select value={mes} onChange={e => setMes(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Todos</option>
              {MESES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="field" style={{ width: 160 }}>
            <label>Situação</label>
            <select value={situacao} onChange={e => setSituacao(e.target.value)}>
              <option value="">Todas</option>
              <option value="0">Aberto</option>
              <option value="1">Fechado</option>
            </select>
          </div>
          <button className="btn btn-outline" onClick={() => carregar(1)}>Filtrar</button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto/importar-lote')}>
            📡 Importar BUSREP em Lote
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto/imprimir-lote')}>
            🖨️ Imprimir em Lote
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto/lote')}>
            Abrir em Lote
          </button>
          <button className="btn btn-outline" style={{ color: '#b91c1c' }} onClick={() => navigate('/funcionarios/ficha-ponto/excluir-lote')}>
            🗑️ Excluir em Lote
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/funcionarios/ficha-ponto/nova')}>
            + Incluir
          </button>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            {carregando ? 'Carregando...' : `${total} ficha(s) encontrada(s)`}
          </span>
          {paginacao}
        </div>
        {!carregando && lista.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhuma ficha ponto encontrada.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {lista.map(f => (
            <div
              key={`${f.ano}-${f.mes}-${f.colaborador}`}
              style={{
                border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {f.colaborador} - {f.nome || '(colaborador não encontrado)'}
                  <SituacaoBadge situacao={f.situacao} />
                </div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <span>{MESES[f.mes]}/{f.ano}</span>
                  {f.totais && CAMPOS_TOTAIS_FICHA.map(({ campo, sigla, descricao }) => (
                    <span key={campo}>
                      <abbr title={descricao} style={{ textDecoration: 'underline dotted', cursor: 'help' }}>{sigla}</abbr>
                      {' '}<b style={{ color: '#374151' }}>{minutosParaHM(f.totais![campo])}</b>
                    </span>
                  ))}
                </div>
                {f.banco_horas && f.banco_horas.length > 0 && (
                  <div style={{ fontSize: '0.75rem', color: '#374151', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ color: '#9ca3af' }}>Banco de horas:</span>
                    {f.banco_horas.map(p => (
                      <span key={p.dias}>{p.dias}d <b>{minutosParaHM(p.saldoMinutos)}</b></span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate(`/funcionarios/ficha-ponto/${f.ano}/${f.mes}/${f.colaborador}`)}
                >
                  Abrir
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  style={{ color: '#b91c1c' }}
                  onClick={() => excluir(f)}
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
        {paginacao && <div style={{ marginTop: 10 }}>{paginacao}</div>}
      </div>
    </div>
  )
}
